import type { BulkUserRow } from '@/types/bulkUserImport';
import { suggestEmailFromName } from '@/utils/provisionedUserEmail';

function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (ch === ',' && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
}

function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/\s+/g, '_');
}

function titleCase(word: string): string {
  if (!word) return '';
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

function parseName(value: string): { firstName: string; lastName: string } {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: titleCase(parts[0]), lastName: '' };
  return { firstName: titleCase(parts[0]), lastName: parts.slice(1).map(titleCase).join(' ') };
}

const HEADER_ALIASES: Record<string, keyof BulkUserRow | 'name' | 'full_name'> = {
  first_name: 'firstName',
  firstname: 'firstName',
  first: 'firstName',
  last_name: 'lastName',
  lastname: 'lastName',
  last: 'lastName',
  surname: 'lastName',
  name: 'name',
  full_name: 'name',
  email: 'email',
  email_address: 'email',
  user_group: 'userGroup',
  group: 'userGroup',
  role: 'userGroup',
  department: 'department',
  dept: 'department',
  card_number: 'cardNumber',
  cardnumber: 'cardNumber',
};

export function parseBulkUserCsv(raw: string, creatorEmail = 'admin@assaabloy.com'): BulkUserRow[] {
  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];

  const headerCells = splitCsvLine(lines[0]).map(normalizeHeader);
  const hasKnownHeader = headerCells.some((h) => h in HEADER_ALIASES);

  const dataLines = hasKnownHeader ? lines.slice(1) : lines;
  const headers = hasKnownHeader ? headerCells : ['name', 'email', 'user_group', 'department'];

  const rows: BulkUserRow[] = [];

  dataLines.forEach((line, index) => {
    const cells = splitCsvLine(line);
    if (cells.every((c) => !c)) return;

    const record: Record<string, string> = {};
    headers.forEach((h, i) => {
      record[h] = cells[i]?.trim() ?? '';
    });

    let firstName = '';
    let lastName = '';
    const nameField = record.name || record.full_name || '';
    if (record.first_name || record.firstname || record.first) {
      firstName = (record.first_name || record.firstname || record.first).trim();
      lastName = (record.last_name || record.lastname || record.last || record.surname || '').trim();
    } else if (nameField) {
      ({ firstName, lastName } = parseName(nameField));
    } else if (cells[0] && !cells[0].includes('@')) {
      ({ firstName, lastName } = parseName(cells[0]));
    }

    const email =
      record.email ||
      record.email_address ||
      (cells.find((c) => c.includes('@')) ?? '') ||
      suggestEmailFromName(firstName, lastName, creatorEmail);

    if (!firstName && !email) return;

    const cardRaw = record.card_number || record.cardnumber || '';
    const cardNumber = cardRaw && !/^\d{4,8}$/.test(cardRaw) ? cardRaw.slice(-5).padStart(5, '0') : undefined;
    const deviceInfo = (record.deviceinfo || record.device_info || '').trim();
    const credentialStatus = (record.credentialstatus || record.credential_status || '').trim();
    const pinRaw = record.pin || '';
    const hasPin =
      /pin/i.test(credentialStatus) ||
      Boolean(pinRaw.trim()) ||
      (/^\d{4,8}$/.test(cardRaw) && !cardNumber);

    rows.push({
      id: `bulk-row-${index}`,
      firstName,
      lastName,
      email,
      userGroup: record.user_group || record.group || record.role || 'Viewers',
      department: record.department || record.dept || '',
      cardNumber: cardNumber ?? (hasPin ? undefined : cardRaw ? cardRaw.slice(-5).padStart(5, '0') : undefined),
      deviceInfo: deviceInfo || undefined,
      hasMobile: /iphone|android|samsung|mobile|phone/i.test(deviceInfo.toLowerCase()),
      hasPin,
      status: 'ready',
    });
  });

  return rows;
}
